/**
 * M8 — Ops Insights (Tier 1)
 * ===========================
 *
 * Single-page ops insights surface composing 8 read-only widgets:
 *   1. Live activity ticker
 *   2. Funnel chart (Searched → Ticketed)
 *   3. Supplier health board
 *   4. Live GMV counters
 *   5. PNR quick-search (top bar)
 *   6. Auto-resolution digest
 *   7. Stuck bookings (holds + pax)
 *   8. Combined log viewer (audit / supplier / events)
 *
 * Auto-refreshes every 15 s. Theme matches the rest of the admin
 * panel (#1B375B navy, #659A9B teal, modern gradients).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  TrendingUp, ServerCrash, IndianRupee, Search, Bot,
  AlertTriangle, FileText, Loader2, RefreshCw, Zap,
  Plane, CheckCircle, X, Clock,
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { fmtIST, fmtISTShort, fmtISTClock } from '../../utils/datetimeIST';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/monitor/insights`;

const FUNNEL_WIRE_POLL_MS = 4000;
const SLOW_POLL_MS = 15_000;

const _money = (n) => {
  if (n === null || n === undefined) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
};
const _date = fmtISTShort;
const _shortTs = fmtISTClock;

// ─── 5. Quick search ──────────────────────────────────────────────────
function QuickSearch() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) { setRows([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/quick-search`, {
          params: { q, limit: 12 }, withCredentials: true,
        });
        setRows(r.data.rows || []);
        setOpen(true);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative" data-testid="quick-search-wrap">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => rows.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Quick-search · PNR / booking-id / supplier-id / email / phone"
        className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#659A9B] focus:outline-none"
        data-testid="quick-search-input"
      />
      {loading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-gray-400" />}
      {open && rows.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-2xl max-h-[420px] overflow-y-auto" data-testid="quick-search-results">
          {rows.map((r) => (
            <button
              key={r.booking_id}
              onClick={() => navigate(`/admin/ops-monitor?focus=${r.booking_id}`)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
              data-testid={`qs-row-${r.booking_id}`}
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-mono font-bold text-[#1B375B]">{r.booking_id}</span>
                <span className="text-[10px] font-bold uppercase text-gray-500">{r.user_type}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-gray-600 mt-0.5">
                {r.pnr && <span className="font-mono">PNR · {r.pnr}</span>}
                {r.supplier_id && <span className="font-mono text-gray-400">· {r.supplier_id}</span>}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                <span>{r.name || r.email || r.phone || '—'}</span>
                {r.route && r.route !== ' → ' && <span>· {r.route}</span>}
                <span className="ml-auto font-semibold text-gray-700">{_money(r.amount)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── 4. Live GMV counters ─────────────────────────────────────────────
function GmvCounters({ data, loading }) {
  if (loading || !data) {
    return <div className="h-32 bg-gray-100 rounded-xl animate-pulse" data-testid="gmv-loading" />;
  }
  const w = data.windows || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="gmv-counters">
      {['today', 'week', 'month'].map((label) => {
        const it = w[label] || {};
        return (
          <div key={label} className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-[#F7FBFC] p-4" data-testid={`gmv-${label}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase font-bold text-[#1B375B] tracking-wider">{label === 'today' ? 'Last 24 h' : label === 'week' ? 'Last 7 d' : 'Last 30 d'}</p>
              <IndianRupee className="w-4 h-4 text-[#659A9B]" />
            </div>
            <p className="text-2xl font-extrabold text-[#1B375B]">{_money(it.total_gmv)}</p>
            <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
              <div>
                <p className="text-gray-500">B2C</p>
                <p className="font-bold text-gray-800">{_money(it.b2c_gmv)} · {it.b2c_count || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Agent</p>
                <p className="font-bold text-gray-800">{_money(it.agent_gmv)} · {it.agent_count || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Refunded</p>
                <p className="font-bold text-rose-700">{_money(it.refund_gmv)}</p>
              </div>
              <div>
                <p className="text-gray-500">Net GMV</p>
                <p className="font-bold text-gray-800">{_money(it.net_gmv)}</p>
              </div>
              <div>
                <p className="text-gray-500">Net Revenue</p>
                <p className="font-bold text-emerald-700">{_money(it.net_revenue ?? it.commission)}</p>
              </div>
              <div>
                <p className="text-gray-500">Take Rate</p>
                <p className="font-bold text-indigo-700">{it.take_rate_pct != null ? `${it.take_rate_pct}%` : '—'}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── 2. Funnel chart ──────────────────────────────────────────────────
function FunnelChart({ data, loading, window, onWindowChange }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  if (loading || !data) {
    return <div className="h-72 bg-gray-100 rounded-xl animate-pulse" />;
  }
  const max = Math.max(...(data.buckets || []).map((b) => b.count), 1);
  const tone = {
    searched:  'from-gray-300 to-gray-400',
    payment:   'from-blue-400 to-blue-500',
    supplier:  'from-amber-400 to-amber-500',
    pnr:       'from-orange-400 to-orange-500',
    ticketed:  'from-emerald-500 to-emerald-600',
    cancelled: 'from-rose-400 to-rose-500',
    failed:    'from-rose-500 to-rose-700',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="funnel-chart">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1B375B] inline-flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#659A9B]" />
          Conversion Funnel · {data.total} total
        </h3>
        <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
          {['today', 'week', 'month'].map((w) => (
            <button
              key={w}
              onClick={() => onWindowChange(w)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${window === w ? 'bg-white text-[#1B375B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`funnel-window-${w}`}
            >
              {w === 'today' ? '24h' : w === 'week' ? '7d' : '30d'}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5" data-testid="funnel-bars">
        {data.buckets.map((b, i) => {
          const pct = (b.count / max) * 100;
          return (
            <div key={b.bucket} className="flex items-center gap-2 text-xs" style={{ animation: mounted ? `slideRight 0.5s ${i * 50}ms forwards` : 'none' }}>
              <div className="w-20 text-right font-semibold text-gray-600 capitalize">{b.bucket}</div>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${tone[b.bucket] || tone.searched} transition-all duration-700 ease-out`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-12 text-right font-bold text-[#1B375B]">{b.count}</div>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes slideRight { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </div>
  );
}

// ─── 3. Supplier health board ─────────────────────────────────────────
function SupplierHealth({ data, loading, window, onWindowChange }) {
  if (loading || !data) {
    return <div className="h-72 bg-gray-100 rounded-xl animate-pulse" />;
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="supplier-health">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#1B375B] inline-flex items-center gap-2">
          <ServerCrash className="w-4 h-4 text-[#659A9B]" />
          Supplier Health
        </h3>
        <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
          {['today', 'week', 'month'].map((w) => (
            <button
              key={w}
              onClick={() => onWindowChange(w)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md ${window === w ? 'bg-white text-[#1B375B] shadow-sm' : 'text-gray-500'}`}
              data-testid={`sup-health-window-${w}`}
            >
              {w === 'today' ? '24h' : w === 'week' ? '7d' : '30d'}
            </button>
          ))}
        </div>
      </div>
      {(data.suppliers || []).length === 0 ? (
        <p className="text-xs text-gray-500 py-4 text-center">No supplier API calls in this window.</p>
      ) : (
        <div className="space-y-2">
          {data.suppliers.map((s) => {
            const rateColor = s.success_rate >= 95 ? 'text-emerald-700 bg-emerald-100' :
                              s.success_rate >= 80 ? 'text-amber-700 bg-amber-100' :
                              'text-rose-700 bg-rose-100';
            return (
              <div key={s.supplier} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors" data-testid={`sup-row-${s.supplier}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-sm text-[#1B375B] uppercase">{s.supplier}</p>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${rateColor}`}>{s.success_rate}% SR</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div><p className="text-gray-500 text-[10px]">Calls</p><p className="font-bold">{s.calls}</p></div>
                  <div><p className="text-gray-500 text-[10px]">Failures</p><p className={`font-bold ${s.failures > 0 ? 'text-rose-700' : 'text-gray-800'}`}>{s.failures}</p></div>
                  <div><p className="text-gray-500 text-[10px]">P50 / P95</p><p className="font-bold">{s.p50_ms || 0}ms / {s.p95_ms || 0}ms</p></div>
                  <div><p className="text-gray-500 text-[10px]">Max latency</p><p className="font-bold">{s.max_ms || 0}ms</p></div>
                </div>
                {s.top_errors && s.top_errors.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.top_errors.map((e) => (
                      <span key={e.code} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100">{e.code} · {e.count}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── 1. Pre-book funnel wire (fast poll) ─────────────────────────────
function FunnelWireTicker({ rows, loading }) {
  const _trunc = (s) => {
    if (!s) return '—';
    const t = String(s);
    return t.length > 10 ? `${t.slice(0, 10)}…` : t;
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 h-full flex flex-col" data-testid="funnel-wire-ticker">
      <h3 className="text-sm font-bold text-[#1B375B] mb-2 inline-flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#659A9B]" />
        Live funnel wire
        <span className="ml-auto text-[10px] font-mono text-emerald-600 inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
          ~4s
        </span>
      </h3>
      <p className="text-[10px] text-gray-500 mb-2">Pre-book signals (Tripjack / Riya): fare alerts, validate failures, supplier rejects.</p>
      <div className="flex-1 overflow-y-auto max-h-[420px] space-y-1.5">
        {loading && rows.length === 0 && <p className="text-xs text-gray-400 py-2">Loading…</p>}
        {!loading && rows.length === 0 && <p className="text-xs text-gray-400 py-2 text-center">No funnel events yet.</p>}
        {rows.map((r, i) => (
          <div
            key={`${r.correlation_id}-${r.ts}-${i}`}
            className="text-[11px] border-l-2 border-amber-400 pl-2 py-1 hover:bg-gray-50 transition-colors"
            data-testid={`funnel-wire-row-${i}`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="font-bold text-[#1B375B] uppercase text-[10px]">{r.supplier || '—'}</span>
              <span className="text-gray-400 font-mono text-[10px] shrink-0">{_shortTs(r.ts)}</span>
            </div>
            <div className="flex flex-wrap gap-x-2 text-[10px] text-gray-600">
              <span className="font-semibold text-gray-800">{r.event_type}</span>
              {r.stage && <span className="text-gray-400">· {r.stage}</span>}
            </div>
            <p className="text-gray-700 truncate" title={r.message}>{r.message || '—'}</p>
            <p className="font-mono text-gray-400 text-[10px] truncate" title={r.correlation_id || ''}>
              sess {_trunc(r.correlation_id)}
              {r.booking_id ? ` · ${r.booking_id}` : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 6. Auto-resolution ───────────────────────────────────────────────
function AutoResolution({ data, loading }) {
  if (loading || !data) return <div className="h-48 bg-gray-100 rounded-xl animate-pulse" />;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="auto-resolution">
      <h3 className="text-sm font-bold text-[#1B375B] mb-3 inline-flex items-center gap-2">
        <Bot className="w-4 h-4 text-[#659A9B]" />
        Auto-resolved · {data.hours}h
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid="auto-pnr-recovered">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase font-bold text-emerald-800">PNR recovered</p>
            <CheckCircle className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-2xl font-extrabold text-emerald-900 mt-1">{data.totals.pnr_recovered}</p>
          <p className="text-[10px] text-emerald-700">by PNR-recovery cron</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="auto-hold-released">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase font-bold text-amber-800">Hold released</p>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-2xl font-extrabold text-amber-900 mt-1">{data.totals.hold_released}</p>
          <p className="text-[10px] text-amber-700">by hold-expiry cron</p>
        </div>
      </div>
      {data.pnr_recovered.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Recently recovered</p>
          <ul className="space-y-1 text-[11px]">
            {data.pnr_recovered.slice(0, 5).map((b) => (
              <li key={b.booking_id} className="font-mono text-gray-700 flex justify-between">
                <span>{b.booking_id}</span>
                <span className="text-emerald-700 font-bold">{b.pnr || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── 7. Stuck bookings ────────────────────────────────────────────────
function StuckBookings({ data, loading }) {
  const navigate = useNavigate();
  if (loading || !data) return <div className="h-72 bg-gray-100 rounded-xl animate-pulse" />;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="stuck-bookings">
      <h3 className="text-sm font-bold text-[#1B375B] mb-3 inline-flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-rose-600" />
        Stuck bookings
      </h3>
      <div className="grid grid-cols-2 gap-2 mb-3 text-center">
        <div className="bg-rose-50 border border-rose-100 rounded-lg p-2" data-testid="stuck-holds-count">
          <p className="text-[10px] uppercase font-bold text-rose-800">Stuck holds</p>
          <p className="text-xl font-extrabold text-rose-900">{data.totals.stuck_holds}</p>
          <p className="text-[9px] text-rose-700">on-hold &gt; {data.hold_hours_threshold}h</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-testid="stuck-pax-count">
          <p className="text-[10px] uppercase font-bold text-amber-800">Stuck pax</p>
          <p className="text-xl font-extrabold text-amber-900">{data.totals.stuck_pax}</p>
          <p className="text-[9px] text-amber-700">incomplete data</p>
        </div>
      </div>
      <div className="space-y-1 max-h-[260px] overflow-y-auto">
        {[...data.stuck_holds.slice(0, 5).map((b) => ({ ...b, kind: 'hold' })),
          ...data.stuck_pax.slice(0, 5).map((b) => ({ ...b, kind: 'pax' }))]
          .map((b, i) => (
          <button
            key={`${b.kind}-${b.booking_id}-${i}`}
            onClick={() => navigate(`/admin/ops-monitor?focus=${b.booking_id}`)}
            className="w-full text-left text-[11px] border border-gray-100 rounded-lg p-2 hover:bg-gray-50"
            data-testid={`stuck-row-${b.kind}-${b.booking_id}`}
          >
            <div className="flex justify-between items-start">
              <span className="font-mono font-bold text-[#1B375B]">{b.booking_id}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${b.kind === 'hold' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>{b.kind}</span>
            </div>
            <p className="text-gray-600 mt-0.5">
              {b.kind === 'hold' && b.stuck_hours && <span>{b.stuck_hours}h on hold · </span>}
              {b.issues && <span>{b.issues.join(', ')}</span>}
              {b.contact && <span>{b.contact}</span>}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── 8. Combined log viewer ───────────────────────────────────────────
function LogViewer() {
  const [kind, setKind] = useState('audit');
  const [search, setSearch] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/logs`, {
        params: {
          kind,
          search: search || undefined,
          correlation_id: correlationId.trim() || undefined,
          limit: 80,
        },
        withCredentials: true,
      });
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [kind, search, correlationId]);
  useEffect(() => { const t = setTimeout(fetchLogs, 200); return () => clearTimeout(t); }, [fetchLogs]);

  const list = data?.logs?.[kind] || [];
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="log-viewer">
      <h3 className="text-sm font-bold text-[#1B375B] mb-3 inline-flex items-center gap-2">
        <FileText className="w-4 h-4 text-[#659A9B]" />
        Log viewer
      </h3>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
          {['audit', 'supplier', 'events'].map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`px-3 py-1 text-[11px] font-semibold rounded-md capitalize ${kind === k ? 'bg-white text-[#1B375B] shadow-sm' : 'text-gray-500'}`}
              data-testid={`log-tab-${k}`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="w-full min-w-[200px] sm:w-auto sm:min-w-[160px]">
          <input
            value={correlationId}
            onChange={(e) => setCorrelationId(e.target.value)}
            placeholder="Correlation id (supplier / events)"
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg font-mono"
            data-testid="log-correlation"
          />
        </div>
        <div className="flex-1 relative min-w-[200px]">
          <Search className="absolute left-2.5 top-2 w-3 h-3 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search logs…"
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg"
            data-testid="log-search"
          />
        </div>
      </div>
      <div className="max-h-[320px] overflow-y-auto" data-testid="log-list">
        {loading && <p className="text-xs text-gray-400 text-center py-4">Loading…</p>}
        {!loading && list.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No {kind} logs.</p>}
        {list.map((row, i) => (
          <div key={i} className="text-[11px] border-b border-gray-100 py-1.5 last:border-0 hover:bg-gray-50 px-2" data-testid={`log-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-[#1B375B]">
                {kind === 'audit' && (row.action || '—')}
                {kind === 'supplier' && `${row.endpoint || '—'} · ${row.http_status || '—'}`}
                {kind === 'events' && (row.type || '—')}
              </span>
              <span className="text-[10px] text-gray-400 font-mono">{_date(row.ts)}</span>
            </div>
            <p className="text-gray-600 truncate">
              {kind === 'audit' && (row.note || `${row.from || ''} → ${row.to || ''}`)}
              {kind === 'supplier' && `${row.supplier || ''} · ${row.latency_ms || 0}ms${row.error_code ? ' · ' + row.error_code : ''}`}
              {kind === 'events' && row.message}
            </p>
            <p className="font-mono text-gray-400 text-[10px] truncate">
              {row.booking_id || '—'} · {row.correlation_id || (row.by || '—')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function OpsInsights() {
  const [funnelWire, setFunnelWire] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [funnelWindow, setFunnelWindow] = useState('today');
  const [supplier, setSupplier] = useState(null);
  const [supWindow, setSupWindow] = useState('today');
  const [gmv, setGmv] = useState(null);
  const [autoRes, setAutoRes] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [loading, setLoading] = useState({ funnelWire: true, funnel: true, supplier: true, gmv: true, auto: true, stuck: true });
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchFunnelWire = useCallback(async (quiet = false) => {
    if (!quiet) setLoading((l) => ({ ...l, funnelWire: true }));
    try {
      const r = await axios.get(`${API}/funnel-activity`, {
        params: { limit: 100, since_seconds: 3600 },
        withCredentials: true,
      });
      setFunnelWire(r.data.rows || []);
    } catch (e) {
      if (!quiet) console.error('[insights] funnel-activity failed', e);
    } finally {
      setLoading((l) => ({ ...l, funnelWire: false }));
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLastRefresh(new Date().toISOString());
    const calls = [
      ['funnel', `${API}/funnel?window=${funnelWindow}`],
      ['supplier', `${API}/supplier-health?window=${supWindow}`],
      ['gmv', `${API}/gmv`],
      ['auto', `${API}/auto-resolution?hours=24`],
      ['stuck', `${API}/stuck?hold_hours=2`],
    ];
    await Promise.all(calls.map(async ([key, url]) => {
      try {
        const r = await axios.get(url, { withCredentials: true });
        if (key === 'funnel') setFunnel(r.data);
        if (key === 'supplier') setSupplier(r.data);
        if (key === 'gmv') setGmv(r.data);
        if (key === 'auto') setAutoRes(r.data);
        if (key === 'stuck') setStuck(r.data);
      } catch (e) {
        console.error(`[insights] ${key} failed`, e);
      } finally {
        setLoading((l) => ({ ...l, [key]: false }));
      }
    }));
    fetchFunnelWire(true);
  }, [funnelWindow, supWindow, fetchFunnelWire]);

  useEffect(() => {
    fetchAll();
    const slow = setInterval(fetchAll, SLOW_POLL_MS);
    return () => clearInterval(slow);
  }, [fetchAll]);

  useEffect(() => {
    fetchFunnelWire(false);
    const fast = setInterval(() => { fetchFunnelWire(true); }, FUNNEL_WIRE_POLL_MS);
    return () => clearInterval(fast);
  }, [fetchFunnelWire]);

  return (
    <div className="px-4 py-5 space-y-4" data-testid="ops-insights">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Zap className="w-6 h-6 text-[#659A9B]" />
            Ops Insights
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Funnel chart &amp; health widgets refresh every {SLOW_POLL_MS / 1000} s · pre-book funnel wire ~{' '}
            {FUNNEL_WIRE_POLL_MS / 1000}s · last refresh {lastRefresh ? _shortTs(lastRefresh) : '—'}
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1B375B] text-white text-sm font-semibold rounded-lg hover:bg-[#0f2742]"
          data-testid="insights-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {/* Quick search */}
      <QuickSearch />

      {/* GMV strip */}
      <GmvCounters data={gmv} loading={loading.gmv} />

      {/* Funnel chart + funnel wire */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><FunnelChart data={funnel} loading={loading.funnel} window={funnelWindow} onWindowChange={setFunnelWindow} /></div>
        <div><FunnelWireTicker rows={funnelWire} loading={loading.funnelWire} /></div>
      </div>

      {/* Supplier health + Stuck + Auto-resolution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SupplierHealth data={supplier} loading={loading.supplier} window={supWindow} onWindowChange={setSupWindow} />
        <StuckBookings data={stuck} loading={loading.stuck} />
        <AutoResolution data={autoRes} loading={loading.auto} />
      </div>

      {/* Log viewer full-width */}
      <LogViewer />
    </div>
  );
}
